import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import GlobalStyles from './styles/GlobalStyles';
import Navbar from './components/Navbar';
import Section, { VisibleSectionProvider } from './components/Section';
import CountdownTimer from './components/CountdownTimer';
import slubImage from './assets/images/slub.jpg';
import weseleImage from './assets/images/wesele.jpg';
import GoogleMapComponent from './components/GoogleMapComponent';
import harmonogramImage from './assets/images/harmonogram.svg';
import ContactSection from './components/ContactSection';
import RSVPBanner from './components/RSVPBanner';
import Gallery from './components/Gallery';
import FloatingButton from './components/FloatingButton';

const App: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [fullscreenGallery, setFullscreenGallery] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
  
    const weddingDate = new Date("2025-08-30T18:00:00");
    const now = new Date();
    if (now < weddingDate) return;
  
    const container = containerRef.current;
    const gallery = document.getElementById("galeria");
    if (!gallery) return;
  
    const prefersReduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  
    // Czy przewija kontener (desktop), czy cała strona (mobile)?
    const scrollerIsContainer =
      !!container && container.scrollHeight > container.clientHeight + 1;
  
    // Oblicz docelowy top w zależności od „scroller-a”
    const getTargetTop = () => {
      if (scrollerIsContainer && container) {
        // Pozycja galerii względem kontenera
        // (offsetTop kontenera odejmujemy, żeby wyjść z układu strony)
        const containerTop = container.getBoundingClientRect().top + (window.pageYOffset || 0);
        const galleryTop = gallery.getBoundingClientRect().top + (window.pageYOffset || 0);
        return (galleryTop - containerTop) + container.scrollTop;
      } else {
        // Przewija całe okno (mobile)
        const pageY = window.pageYOffset || document.documentElement.scrollTop || 0;
        return pageY + gallery.getBoundingClientRect().top;
      }
    };
  
    const scrollNow = () => {
      const top = getTargetTop();
  
      if (scrollerIsContainer && container) {
        container.scrollTo({ top, behavior: prefersReduced ? "auto" : "smooth" });
      } else {
        window.scrollTo({ top, behavior: prefersReduced ? "auto" : "smooth" });
      }
    };
  
    let attempts = 0;
    let rafId = 0;
  
    const tick = () => {
      const before = scrollerIsContainer ? container!.scrollTop : (window.pageYOffset || 0);
      scrollNow();
      attempts += 1;
  
      const after = scrollerIsContainer ? container!.scrollTop : (window.pageYOffset || 0);
      const diff = Math.abs(after - before);
      if (diff < 1 || attempts >= 20) return;
  
      rafId = requestAnimationFrame(tick);
    };
  
    rafId = requestAnimationFrame(tick);
  
    return () => cancelAnimationFrame(rafId);
  }, []);  

  return (
    <>
      <VisibleSectionProvider>
        <GlobalStyles />
        <Navbar />
        <ScrollableContainer ref={containerRef}>
          <CountdownTimer />
          <Section id="slub" title="Ślub">
            <SectionContent>
              <ImageWrapper>
                <Image src={slubImage} alt="Ślub" />
              </ImageWrapper>
              <TextWrapper>
                <Description>
                  30 sierpnia 2025<br />
                  Sobota 16:00<br />
                  Bazylika Katedralna Wniebowzięcia Najświętszej Maryi Panny<br />
                  Tumska 1, Płock
                </Description>
              </TextWrapper>
            </SectionContent>
            <MapWrapper id="map-wrapper">
              <GoogleMapComponent
                latitude={52.541535}
                longitude={19.689036}
                destinationName="Bazylika Katedralna Wniebowzięcia Najświętszej Maryi Panny, Tumska 1, 09-400 Płock"
              />
            </MapWrapper>
          </Section>
          <Section id="wesele" title="Wesele">
            <SectionContent className="wesele">
              <ImageWrapper className="wesele-image-wrapper">
                <Image src={weseleImage} alt="Ślub" />
              </ImageWrapper>
              <TextWrapper>
                <Description>
                  Po ceremonii zapraszamy Was <br />
                  na wesele do białego rana! <br /> Gdzie? <br />
                  Hotel Podkowa <br /> przy ulicy Zglenickiego 13 w Płocku
                </Description>
              </TextWrapper>
            </SectionContent>
            <MapWrapper id="map-wrapper-wesele">
              <GoogleMapComponent
                latitude={52.573376}
                longitude={19.646601}
                destinationName="PODKOWA Witolda Zglenickiego 13, Płock"
              />
            </MapWrapper>
          </Section>
          <Section id="program" title="Program">
            <SectionContent className="program">
              <ImageWrapper className="program-image-wrapper">
                <img src={harmonogramImage} alt="Harmonogram" className="harmonogram-image" />
              </ImageWrapper>
              <TextWrapper>
                <Description className="program-description">
                  * Fotolustro będzie dostępne przez 3 godziny od <strong>20:00</strong> do <strong>23:00</strong><br />
                  * Barman będzie serwował drinki od godziny <strong>18:00</strong> do <strong>2:00</strong>
                </Description>
              </TextWrapper>
            </SectionContent>
          </Section>
          <Section id="szczegoly" title="Szczegóły">
            <SectionContent>
              <DetailsText>
                <h3>Nocleg 🛌</h3>
                <p>
                  Dla naszych gości z dalszych stron przygotowaliśmy pokoje w tym samym miejscu, gdzie odbywa się wesele.<br />
                  Doba hotelowa zaczyna się o <strong>14:00</strong>, a kończy o <strong>12:00</strong> następnego dnia.<br />
                  W niedzielę śniadanie będzie serwowane do <strong>10:30</strong>.
                </p>
                <h3>Poprawiny 🎉</h3>
                <p>
                  Nie kończymy na jednym dniu!<br />
                  W niedzielę zapraszamy Was na poprawiny, które odbędą się w tym samym miejscu co wesele.
                  Rozpoczynamy o <strong>13:00</strong>, a kończymy około <strong>19:00</strong>.<br />
                </p>
              </DetailsText>
            </SectionContent>
          </Section>
          <Section id="kontakt" title="Kontakt">
            <SectionContent>
              <ContactSection />
            </SectionContent>
          </Section>
          <Section id="galeria" title="Galeria">
            <SectionContent className="upload-section">
              <Gallery setFullscreenGallery={setFullscreenGallery} />
            </SectionContent>
          </Section>
        </ScrollableContainer>
        <FloatingButton fullscreenGallery={fullscreenGallery} />
        <RSVPBanner isEnabled={false} />
      </VisibleSectionProvider>
    </>
  );
};

const ScrollableContainer = styled.div`
  height: 100dvh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  & > * {
    flex-shrink: 0;
    min-height: 100vh; /* Changed from height to min-height */
  }

  & > #galeria {
    height: auto;
    min-height: 100vh;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    height: auto;

    & > * {
      height: auto;
      padding-bottom: 20px;
    }
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  &.harmonogram {
    display: flex;
    justify-content: space-around; /* Ustawienie dla harmonogramu */
    align-items: center;
    width: 100%; /* Dodaj szerokość, aby elementy mogły się rozłożyć */
  }

  &.wesele {
    @media (max-width: 768px) {
      flex-direction: column; /* Układ pionowy tylko dla sekcji "Wesele" */
      align-items: center;
      text-align: center;
    }
  }

  &.program {
    flex-direction: column; /* Układ pionowy dla sekcji "Program" */
    align-items: center;
    text-align: center;

    @media (max-width: 768px) {
      gap: 15px; /* Zmniejszenie odstępów na telefonach */
    }
  }

  &.upload-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }
`;

const ImageWrapper = styled.div`
  flex: 1;
  max-width: 100%;
  display: flex;
  justify-content: center;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 10px;
  }

  @media (max-width: 768px) {
    width: 12rem; /* Domyślny rozmiar dla innych obrazków */
    img {
      object-fit: cover;
      height: 100%;
      width: auto;
    }
  }

  @media (min-width: 768px) {
    max-width: 30%; /* Domyślny rozmiar dla innych obrazków */
  }

  &.wesele-image-wrapper {
    @media (max-width: 768px) {
      width: 18rem; /* Zwiększenie szerokości obrazka w sekcji "Wesele" na mobilnym widoku */
    }

    @media (min-width: 768px) {
      max-width: 50%; /* Zwiększenie szerokości obrazka w sekcji "Wesele" na desktopowym widoku */
    }
  }

  &.program-image-wrapper {
    @media (min-width: 768px) {
      max-width: 50%; /* Przywrócenie rozmiaru obrazka w sekcji "Program" na desktopie */
    }

    @media (max-width: 768px) {
      width: 20rem; /* Zwiększenie szerokości obrazka w sekcji "Program" na mobilnym widoku */
    }
  }
`;

const TextWrapper = styled.div`
  flex: 1;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;

  p {
    font-size: 1.2rem;
    color: #333;
    line-height: 1.6;
  }

  @media (min-width: 768px) {
    text-align: left; /* Wyrównuje tekst do lewej na desktopie */
    display: flex;
    justify-content: center;
  }

  @media (max-width: 768px) {
    p {
      font-size: 0.90rem;
    }
  }
`;

const MapWrapper = styled.div.attrs(() => ({
  id: 'map-wrapper', // Dodaj ID dla mapy
}))`
  width: 100%;
  margin-top: 20px;
  display: flex;
  justify-content: center;

  iframe {
    width: 100%;
    max-width: 800px;
    height: 400px;
    border: none;
    border-radius: 10px;
  }
`;

const Description = styled.p`
  font-size: 1.5rem;
  color: #333;
  line-height: 1.8;
  text-align: center;
  margin: 20px 0;

  &.program-description {
    font-size: 0.8rem; /* Zmniejszenie rozmiaru tekstu dla sekcji "Program" */
    line-height: 1.6;
    margin: 15px 0;
  }
`;

const Image = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 10px;
`;

const DetailsText = styled.div`
  margin: 0 auto; /* Wyśrodkowanie */
  text-align: left;
  animation: fade-in 1s ease-in-out;

  h3 {
    font-size: 1.3rem;
    color: #333;
    margin-bottom: 10px;
    border-bottom: 2px solid #ddd; /* Subtelne podkreślenie */
    padding-bottom: 5px;
  }

  p {
    font-size: 1rem;
    color: #555;
    line-height: 1.8;
    margin-bottom: 20px;
  }

  strong {
    color: #007bff; /* Wyróżnienie ważnych informacji */
  }

  blockquote {
    font-style: italic;
    color: #777;
    margin: 10px 0;
    padding-left: 20px;
    border-left: 4px solid #007bff;
  }

  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Styl dla mobilnych urządzeń */
  @media (max-width: 768px) {
    h3 {
      font-size: 1.2rem; /* Zmniejszenie rozmiaru nagłówków */
      margin-bottom: 8px;
    }

    p {
      font-size: 1rem; /* Zmniejszenie rozmiaru tekstu */
      line-height: 1.3; /* Zmniejszenie wysokości linii */
      margin-bottom: 15px;
    }

    blockquote {
      font-size: 0.9rem; /* Zmniejszenie rozmiaru cytatu */
      padding-left: 15px; /* Zmniejszenie odstępu */
    }
  }
`;

const StyledBlockquote = styled.div`
  font-style: italic;
  color: #555;
  margin: 20px 0;
  padding: 5px 5px;
  text-align: center; /* Wyśrodkowanie tekstu */
  line-height: 1.5;
`;

const MediaWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #ddd;

  img, video {
    width: 100%;
    height: auto;
    object-fit: cover;
  }
`;

export default App;